import { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Plus, Trash2, Edit2, Radio, Power, Info, Upload, FileSpreadsheet, AlertTriangle, Check, X, Search, Mic, Headphones, ChevronDown, ChevronRight, Lock, Waves, Zap, Loader2, Package, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { systemsAPI, equipmentAPI } from "@/lib/api";
import { UpgradeModal } from "@/components/UpgradeModal";
import { RackView } from "@/components/systems/RackView";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const StatusBadge = ({ status }) => {
  const colors = {
    stable: "bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30",
    warning: "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30",
    interference: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30",
  };
  const labels = {
    stable: "ESTABLE",
    warning: "RIESGO",
    interference: "INTERFERENCIA",
  };
  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-sm border ${
        colors[status] || colors.stable
      }`}
    >
      {labels[status] || "ESTABLE"}
    </span>
  );
};

export default function SystemsPage({ user, onLogout }) {
  const [systems, setSystems] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipmentDetails, setSelectedEquipmentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [showEquipmentInfo, setShowEquipmentInfo] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // "all", "iem", "microphone"
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    iem: false,
    microphone: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const [form, setForm] = useState({
    name: "",
    equipment_id: "anleon_s3d",
    frequency: "",
    channel_group: "A",
    channel_number: "1",
    band_id: "default",
    system_type: "iem", // "iem" or "microphone"
  });

  // Dynamic channel groups based on equipment
  const [channelGroups, setChannelGroups] = useState(["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T"]);
  const [maxChannelNumber, setMaxChannelNumber] = useState(6);
  const [availableBands, setAvailableBands] = useState({});
  const [presetFrequencies, setPresetFrequencies] = useState([]);
  const [loadingFrequencies, setLoadingFrequencies] = useState(false);

  // Wireless Workbench Import states
  const [showWWBImportDialog, setShowWWBImportDialog] = useState(false);
  const [wwbImportData, setWwbImportData] = useState(null);
  const [wwbImportError, setWwbImportError] = useState("");
  const [wwbImporting, setWwbImporting] = useState(false);
  const [selectedWwbSystems, setSelectedWwbSystems] = useState([]);

  // Spectrum Analysis states
  const [savedScans, setSavedScans] = useState([]);
  const [selectedScanId, setSelectedScanId] = useState("");
  const [analyzingSpectrum, setAnalyzingSpectrum] = useState(false);
  const [spectrumSuggestions, setSpectrumSuggestions] = useState(null);
  const [showSpectrumPanel, setShowSpectrumPanel] = useState(false);

  // Subscription state
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // View mode: "list" or "racks"
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("avx_systems_view") || "list";
  });
  
  // Save view mode preference
  useEffect(() => {
    localStorage.setItem("avx_systems_view", viewMode);
  }, [viewMode]);

  const fetchSubscriptionUsage = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/subscriptions/usage`, getAuthHeaders());
      setSubscriptionUsage(response.data);
    } catch (err) {
      // Non-critical: subscription info optional for basic functionality
      if (process.env.NODE_ENV === 'development') {
        console.debug('Subscription usage fetch failed:', err.message);
      }
    }
  }, []);

  const checkSystemsLimit = () => {
    if (!subscriptionUsage) return true; // Allow if not loaded yet
    const { current, limit, unlimited } = subscriptionUsage.usage.systems;
    if (!unlimited && current >= limit) {
      setShowUpgradeModal(true);
      return false;
    }
    return true;
  };

  // Fetch equipment list on mount
  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const response = await equipmentAPI.getAll();
        setEquipmentList(response.data.equipment || []);
      } catch (err) {
        // Non-critical: equipment list will show empty
        if (process.env.NODE_ENV === 'development') {
          console.debug('Equipment fetch failed:', err.message);
        }
      }
    };
    fetchEquipment();
    fetchSubscriptionUsage();
  }, [fetchSubscriptionUsage]);

  // Update channel options when equipment changes
  useEffect(() => {
    const updateEquipmentConfig = async () => {
      if (!form.equipment_id) return;
      
      try {
        const [channelsRes, bandsRes, detailsRes] = await Promise.all([
          equipmentAPI.getChannels(form.equipment_id),
          equipmentAPI.getBands(form.equipment_id),
          equipmentAPI.getOne(form.equipment_id)
        ]);
        
        const channelConfig = channelsRes.data.channels;
        setChannelGroups(channelConfig.groups || []);
        setMaxChannelNumber(channelConfig.channels_per_group || 6);
        setAvailableBands(bandsRes.data.bands || {});
        
        // Reset to first valid options
        if (!channelConfig.groups.includes(form.channel_group)) {
          setForm(prev => ({ ...prev, channel_group: channelConfig.groups[0] || "A" }));
        }
        if (parseInt(form.channel_number) > channelConfig.channels_per_group) {
          setForm(prev => ({ ...prev, channel_number: "1" }));
        }
        // Set first band if not valid
        const bandKeys = Object.keys(bandsRes.data.bands || {});
        if (bandKeys.length > 0 && !bandKeys.includes(form.band_id)) {
          setForm(prev => ({ ...prev, band_id: bandKeys[0] }));
        }
        
        // Auto-detect system type based on equipment
        const equipmentData = detailsRes.data;
        let detectedType = "iem";
        
        // Check if it's a microphone based on model name or brand
        const modelUpper = (equipmentData.model || "").toUpperCase();
        const fullNameUpper = (equipmentData.full_name || "").toUpperCase();
        
        // Microphone indicators
        const micIndicators = ["QLXD", "ULXD", "SLXD", "BLX", "SKM", "SK ", "EW ", "ATW", "SRC", "SRB"];
        // IEM indicators
        const iemIndicators = ["PSM", "P3T", "P9T", "P10T", "IEM", "SR20", "ANLEON", "RELACART"];
        
        // Check for microphone patterns
        if (micIndicators.some(ind => modelUpper.includes(ind) || fullNameUpper.includes(ind))) {
          detectedType = "microphone";
        }
        // Check for IEM patterns (override if found)
        if (iemIndicators.some(ind => modelUpper.includes(ind) || fullNameUpper.includes(ind))) {
          detectedType = "iem";
        }
        
        // Also check system_type from imported equipment
        if (equipmentData.system_type) {
          detectedType = equipmentData.system_type;
        }
        
        setForm(prev => ({ ...prev, system_type: detectedType }));
        
      } catch (error) {
        // Silently handle equipment config error
      }
    };
    
    updateEquipmentConfig();
  }, [form.equipment_id, form.channel_group, form.channel_number, form.band_id]);

  // Fetch ALL preset frequencies for the equipment (all groups)
  useEffect(() => {
    const fetchAllPresetFrequencies = async () => {
      if (!form.equipment_id) return;
      
      setLoadingFrequencies(true);
      try {
        // Fetch ALL frequencies in a single call (much faster)
        const response = await axios.get(
          `${API_BASE}/equipment/${form.equipment_id}/all-preset-frequencies`,
          getAuthHeaders()
        );
        
        if (response.data.frequencies) {
          setPresetFrequencies(response.data.frequencies);
        } else {
          setPresetFrequencies([]);
        }
      } catch (error) {
        // Silently handle preset frequencies error
        setPresetFrequencies([]);
      } finally {
        setLoadingFrequencies(false);
      }
    };
    
    fetchAllPresetFrequencies();
  }, [form.equipment_id, channelGroups]);

  // Handle frequency selection - auto-set group and channel
  const handleFrequencySelect = (value) => {
    const selectedFreq = presetFrequencies.find(f => f.frequency.toString() === value);
    if (selectedFreq) {
      setForm(prev => ({
        ...prev,
        frequency: value,
        channel_group: selectedFreq.group,
        channel_number: selectedFreq.channel.toString()
      }));
    }
  };

  const fetchSystems = async () => {
    try {
      const response = await systemsAPI.getAll();
      setSystems(response.data);
    } catch (error) {
      toast.error("Error al cargar sistemas");
    } finally {
      setLoading(false);
    }
  };

  // Load saved RF scans
  const fetchSavedScans = async () => {
    try {
      const response = await axios.get(`${API_BASE}/scans`, getAuthHeaders());
      setSavedScans(response.data || []);
    } catch (error) {
      // Error handled silently
    }
  };

  // Analyze spectrum and get clean frequency suggestions
  const analyzeSpectrum = async () => {
    if (!selectedScanId || !form.equipment_id) {
      toast.error("Selecciona un escaneo y un equipo primero");
      return;
    }
    
    setAnalyzingSpectrum(true);
    try {
      const response = await axios.post(
        `${API_BASE}/frequencies/analyze-spectrum`,
        {
          scan_id: selectedScanId,
          equipment_id: form.equipment_id,
          num_frequencies: 20,
          noise_threshold_db: -70,
          min_spacing_mhz: 0.5
        },
        getAuthHeaders()
      );
      
      setSpectrumSuggestions(response.data);
      toast.success(`${response.data.suggested_frequencies.length} frecuencias limpias encontradas`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al analizar espectro";
      toast.error(msg);
    } finally {
      setAnalyzingSpectrum(false);
    }
  };

  useEffect(() => {
    fetchSystems();
    fetchSavedScans();
  }, []);

  // Filter systems based on search and type
  const filteredSystems = systems.filter((system) => {
    // Filter by search term
    const matchesSearch = searchTerm === "" || 
      system.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (system.model && system.model.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filter by type
    const systemType = system.system_type || "iem"; // Default to IEM for backwards compatibility
    const matchesType = activeTab === "all" || systemType === activeTab;
    
    return matchesSearch && matchesType;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get band info for the selected equipment and band
      const bandInfo = availableBands[form.band_id];
      
      const data = {
        name: form.name,
        equipment_id: form.equipment_id,
        model: equipmentList.find(e => e.id === form.equipment_id)?.full_name || "Anleon S3D",
        frequency: parseFloat(form.frequency),
        channel_group: form.channel_group,
        channel_number: parseInt(form.channel_number),
        band_id: form.band_id,
        band_min: bandInfo?.freq_min || 560,
        band_max: bandInfo?.freq_max || 674,
        system_type: form.system_type,
      };

      if (selectedSystem) {
        await systemsAPI.update(selectedSystem.id, data);
        toast.success("Sistema actualizado");
      } else {
        await systemsAPI.create(data);
        toast.success("Sistema creado");
      }
      setShowDialog(false);
      resetForm();
      fetchSystems();
      fetchSubscriptionUsage(); // Refresh usage after creating
    } catch (error) {
      // Check if it's a subscription limit error
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.error === "subscription_limit") {
        setShowDialog(false);
        setShowUpgradeModal(true);
        toast.error(errorDetail.message || "Has alcanzado el límite de tu plan");
      } else {
        const message = typeof errorDetail === 'string' ? errorDetail : errorDetail?.message || "Error al guardar sistema";
        toast.error(message);
      }
    }
  };

  const handleDelete = async () => {
    try {
      await systemsAPI.delete(selectedSystem.id);
      toast.success("Sistema eliminado");
      setShowDeleteDialog(false);
      setSelectedSystem(null);
      fetchSystems();
    } catch (error) {
      toast.error("Error al eliminar sistema");
    }
  };

  const handleToggleActive = async (system) => {
    try {
      await systemsAPI.update(system.id, { is_active: !system.is_active });
      fetchSystems();
      toast.success(
        system.is_active ? "Sistema desactivado" : "Sistema activado"
      );
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      equipment_id: "anleon_s3d",
      frequency: "",
      channel_group: "A",
      channel_number: "1",
      band_id: "default",
      system_type: "iem",
    });
    setSelectedSystem(null);
  };

  const openEditDialog = (system) => {
    setSelectedSystem(system);
    setForm({
      name: system.name,
      equipment_id: system.equipment_id || "anleon_s3d",
      frequency: system.frequency.toString(),
      channel_group: system.channel_group || "A",
      channel_number: (system.channel_number || 1).toString(),
      band_id: system.band_id || "default",
      system_type: system.system_type || "iem",
    });
    setShowDialog(true);
  };

  const viewEquipmentDetails = async (equipmentId) => {
    try {
      const response = await equipmentAPI.getOne(equipmentId);
      setSelectedEquipmentDetails(response.data);
      setShowEquipmentInfo(true);
    } catch (error) {
      toast.error("Error al cargar detalles del equipo");
    }
  };

  const handleDeleteAllSystems = async () => {
    setDeletingAll(true);
    try {
      // Delete all systems one by one
      let successCount = 0;
      let errorCount = 0;
      
      for (const system of systems) {
        try {
          await systemsAPI.delete(system.id);
          successCount++;
        } catch (error) {
          errorCount++;
          // Error handled silently
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} sistemas eliminados correctamente`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} sistemas no pudieron ser eliminados`);
      }
      
      setShowDeleteAllDialog(false);
      fetchSystems();
      fetchSubscriptionUsage();
    } catch (error) {
      toast.error("Error al eliminar los sistemas");
    } finally {
      setDeletingAll(false);
    }
  };

  // Helper function to parse CSV line with quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    
    return result;
  };

  // Parse Wireless Workbench CSV - Supports multiple formats
  const parseWirelessWorkbenchCSV = (csvContent) => {
    // Normalize line endings (handle Windows \r\n, Mac \r, and Unix \n)
    const normalizedContent = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedContent.trim().split('\n');
    const systems = [];
    
    // Detect format type by checking first line
    const firstLine = lines[0].toLowerCase();
    
    // FORMAT 1: Direct CSV with headers (freq,freqName,siteName,zoneName,groupName,tvChannel,modelId,manufacturer,model,range,...)
    if (firstLine.includes('freq') && firstLine.includes('manufacturer')) {
      // Debug log removed
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      
      // Find column indices
      const freqIdx = headers.findIndex(h => h === 'freq');
      const freqNameIdx = headers.findIndex(h => h === 'freqname');
      const manufacturerIdx = headers.findIndex(h => h === 'manufacturer');
      const modelIdx = headers.findIndex(h => h === 'model');
      const rangeIdx = headers.findIndex(h => h === 'range'); // This is the band
      const groupNameIdx = headers.findIndex(h => h === 'groupname');
      const siteNameIdx = headers.findIndex(h => h === 'sitename');
      const tvChannelIdx = headers.findIndex(h => h === 'tvchannel');
      
      // Parse data rows (skip header)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;
        
        // Handle CSV with quoted fields containing commas
        const parts = parseCSVLine(line);
        if (parts.length < 3) continue;
        
        // Extract frequency
        let frequency = 0;
        if (freqIdx >= 0 && parts[freqIdx]) {
          frequency = parseFloat(parts[freqIdx].replace(',', '.'));
        }
        if (frequency === 0 || isNaN(frequency) || frequency < 400 || frequency > 1000) continue;
        
        // Extract name
        let name = `Sistema ${systems.length + 1}`;
        if (freqNameIdx >= 0 && parts[freqNameIdx]) {
          name = parts[freqNameIdx];
        }
        
        // Extract manufacturer and model
        let manufacturer = '';
        let model = '';
        if (manufacturerIdx >= 0 && parts[manufacturerIdx]) {
          manufacturer = parts[manufacturerIdx];
        }
        if (modelIdx >= 0 && parts[modelIdx]) {
          model = parts[modelIdx];
        }
        
        // Extract band/range
        let band = '';
        if (rangeIdx >= 0 && parts[rangeIdx]) {
          band = parts[rangeIdx];
        }
        
        // Extract group name (for system type detection)
        let groupName = '';
        if (groupNameIdx >= 0 && parts[groupNameIdx]) {
          groupName = parts[groupNameIdx];
        }
        
        // Extract site name
        let siteName = '';
        if (siteNameIdx >= 0 && parts[siteNameIdx]) {
          siteName = parts[siteNameIdx];
        }
        
        // Extract TV channel
        let tvChannel = '';
        if (tvChannelIdx >= 0 && parts[tvChannelIdx]) {
          tvChannel = parts[tvChannelIdx];
        }
        
        // Determine system type based on model and group name
        const modelUpper = model.toUpperCase();
        const groupUpper = groupName.toUpperCase();
        const manufacturerUpper = (manufacturer || '').toUpperCase();
        let systemType = 'microphone';
        
        // IEM models detection
        // Shure: PSM series (P3T, P9T, P10T)
        // Sennheiser: ALL EW series are IEM (EW 300, EW 100, EW 500, EW-D, etc.), SR series
        // Generic: Contains "IEM" or "IN-EAR" or "MONITOR"
        if (modelUpper.includes('PSM') || modelUpper.includes('P3T') || 
            modelUpper.includes('P9T') || modelUpper.includes('P10T') ||
            modelUpper.includes('IEM') || modelUpper.includes('SR2050') ||
            modelUpper.includes('SR 2050') ||
            // ALL Sennheiser EW series are IEM
            (manufacturerUpper.includes('SENNHEISER') && modelUpper.includes('EW')) ||
            modelUpper.startsWith('EW') ||
            // Check group name for IEM indicators
            groupUpper.includes('IEM') || groupUpper.includes('IN-EAR') || 
            groupUpper.includes('MONITOR') || groupUpper.includes('INEAR')) {
          systemType = 'iem';
        }
        
        systems.push({
          name: name,
          frequency: frequency,
          channel_group: 'A',
          channel_number: systems.length + 1,
          model: model,
          manufacturer: manufacturer,
          band: band,
          group_name: groupName,
          site_name: siteName,
          tv_channel: tvChannel,
          system_type: systemType,
          equipment_id: null,
        });
      }
      
      return systems;
    }
    
    // FORMAT 2: WWB Report format (has "Primary frequencies" section)
    let inDataSection = false;
    let headerIndex = -1;
    let headers = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineLower = line.toLowerCase();
      
      // Detect start of primary frequencies section
      if (lineLower.includes('primary frequencies')) {
        inDataSection = true;
        continue;
      }
      
      // Detect header row (contains Model, Band, Channel name, Frequency, etc.)
      if (inDataSection && (lineLower.includes('model') || lineLower.includes('channel name') || lineLower.includes('device name'))) {
        headerIndex = i;
        headers = line.split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
        continue;
      }
      
      // Stop at backup frequencies or other sections
      if (inDataSection && (lineLower.includes('backup frequencies') || lineLower.includes('frequency coordination'))) {
        break;
      }
      
      // Parse data rows after header
      if (inDataSection && headerIndex !== -1 && i > headerIndex) {
        const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
        if (parts.length < 2) continue;
        
        // Skip section headers like "No Inclusion Group"
        if (lineLower.includes('inclusion group') || lineLower.includes('exclusion')) continue;
        
        // Find column indices
        const modelIdx = headers.findIndex(h => h.includes('model'));
        const bandIdx = headers.findIndex(h => h.includes('band'));
        const nameIdx = headers.findIndex(h => h.includes('channel name') || h.includes('device name') || h.includes('name'));
        const freqIdx = headers.findIndex(h => h.includes('frequency') || h.includes('freq'));
        const groupChannelIdx = headers.findIndex(h => h.includes('group') && h.includes('channel'));
        const deviceIdIdx = headers.findIndex(h => h.includes('device id'));
        
        // Extract frequency - handle format like "632,125 MHz" or "632.125"
        let frequency = 0;
        
        // First try the frequency column
        if (freqIdx >= 0 && parts[freqIdx]) {
          let freqStr = parts[freqIdx];
          // Remove "MHz" suffix
          freqStr = freqStr.replace(/\s*mhz\s*/gi, '').trim();
          
          // Handle comma as decimal separator (European format)
          // Check if next column might be decimal part (e.g., "632" "125 MHz")
          if (freqIdx + 1 < parts.length) {
            const nextPart = parts[freqIdx + 1].replace(/\s*mhz\s*/gi, '').trim();
            if (/^\d{1,3}$/.test(nextPart)) {
              // Combine: "632" + "125" = "632.125"
              freqStr = freqStr + '.' + nextPart;
            }
          }
          
          // Replace comma with dot for parsing
          freqStr = freqStr.replace(',', '.');
          frequency = parseFloat(freqStr);
        }
        
        // Fallback: scan all parts for frequency value
        if (frequency === 0 || isNaN(frequency)) {
          for (let j = 0; j < parts.length; j++) {
            let part = parts[j].replace(/\s*mhz\s*/gi, '').replace(',', '.').trim();
            
            // Check if this + next part form a frequency
            if (j + 1 < parts.length) {
              const nextPart = parts[j + 1].replace(/\s*mhz\s*/gi, '').trim();
              if (/^\d{1,3}$/.test(part) && /^\d{1,3}$/.test(nextPart)) {
                const combined = parseFloat(part + '.' + nextPart);
                if (combined >= 400 && combined <= 1000) {
                  frequency = combined;
                  break;
                }
              }
            }
            
            const num = parseFloat(part);
            if (!isNaN(num) && num >= 400 && num <= 1000) {
              frequency = num;
              break;
            }
          }
        }
        
        if (frequency === 0 || isNaN(frequency)) continue;
        
        // Extract name
        let name = `Sistema ${systems.length + 1}`;
        if (nameIdx >= 0 && parts[nameIdx] && parts[nameIdx] !== '') {
          name = parts[nameIdx];
        }
        
        // Extract model (raw from WWB)
        let model = '';
        if (modelIdx >= 0 && parts[modelIdx]) {
          model = parts[modelIdx];
        }
        
        // Extract band
        let band = '';
        if (bandIdx >= 0 && parts[bandIdx]) {
          band = parts[bandIdx];
        }
        
        // Extract device ID
        let deviceId = '';
        if (deviceIdIdx >= 0 && parts[deviceIdIdx]) {
          deviceId = parts[deviceIdIdx];
        }
        
        // Extract Group & Channel
        let groupChannel = '';
        let channelGroup = 'A';
        let channelNumber = systems.length + 1;
        if (groupChannelIdx >= 0 && parts[groupChannelIdx]) {
          groupChannel = parts[groupChannelIdx];
          // Parse "G:01 Ch:01" format
          const gcMatch = groupChannel.match(/G:(\d+)\s*Ch:(\d+)/i);
          if (gcMatch) {
            // Convert group number to letter (01 = A, 02 = B, etc.)
            const groupNum = parseInt(gcMatch[1]);
            channelGroup = String.fromCharCode(64 + groupNum); // 1=A, 2=B, etc.
            channelNumber = parseInt(gcMatch[2]);
          }
        }
        
        // Determine system type based on model
        const modelUpper = model.toUpperCase();
        let systemType = 'microphone';
        if (modelUpper.includes('PSM') || modelUpper.includes('P3T') || 
            modelUpper.includes('P9T') || modelUpper.includes('P10T') ||
            modelUpper.includes('IEM') || modelUpper.includes('SR2050')) {
          systemType = 'iem';
        }
        
        systems.push({
          name: name,
          frequency: frequency,
          channel_group: channelGroup,
          channel_number: channelNumber,
          model: model,
          band: band,
          device_id: deviceId,
          group_channel: groupChannel,
          system_type: systemType,
          // Will be populated after equipment import
          equipment_id: null,
        });
      }
    }
    
    // If no systems found with WWB format, try simple format
    if (systems.length === 0) {
      for (const line of lines) {
        const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
        
        // Try to find frequency value
        let frequency = 0;
        for (const part of parts) {
          let cleanPart = part.replace(/\s*mhz\s*/gi, '').replace(',', '.').trim();
          const num = parseFloat(cleanPart);
          if (!isNaN(num) && num >= 400 && num <= 1000) {
            frequency = num;
            break;
          }
        }
        
        if (frequency > 0) {
          const name = parts[0] || `Sistema ${systems.length + 1}`;
          systems.push({
            name: name,
            frequency: frequency,
            channel_group: 'A',
            channel_number: systems.length + 1,
            model: 'Desconocido',
            band: '',
            device_id: '',
            group_channel: '',
            system_type: 'microphone',
            equipment_id: null,
          });
        }
      }
    }
    
    return systems;
  };

  // Handle WWB file selection
  const handleWWBFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setWwbImportError("");
    setWwbImportData(null);
    setSelectedWwbSystems([]);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        // CSV debug removed
        const parsedSystems = parseWirelessWorkbenchCSV(content);
        // Debug log removed
        
        if (parsedSystems.length === 0) {
          setWwbImportError("No se encontraron sistemas válidos en el archivo. Verifica el formato CSV de Wireless Workbench.");
          return;
        }
        
        setWwbImportData(parsedSystems);
        setSelectedWwbSystems(parsedSystems.map((_, i) => i)); // Select all by default
        setShowWWBImportDialog(true);
      } catch (err) {
        // Error handled silently
        setWwbImportError("Error al leer el archivo CSV. Verifica el formato.");
      }
    };
    reader.readAsText(file);
    
    e.target.value = "";
  };

  // Toggle system selection for import
  const toggleWwbSystemSelection = (index) => {
    setSelectedWwbSystems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Confirm WWB import
  const handleConfirmWWBImport = async () => {
    if (!wwbImportData || selectedWwbSystems.length === 0) return;
    
    setWwbImporting(true);
    let successCount = 0;
    let errorCount = 0;
    let equipmentRegistered = [];
    
    for (const index of selectedWwbSystems) {
      const systemData = wwbImportData[index];
      try {
        // Step 1: Register equipment if needed (import from WWB)
        let equipmentId = systemData.equipment_id;
        
        if (!equipmentId && systemData.model) {
          try {
            const importResult = await equipmentAPI.importFromWWB({
              model: systemData.model,
              band: systemData.band || '',
              manufacturer: systemData.manufacturer || '',
            });
            equipmentId = importResult.data.equipment_id;
            
            if (!importResult.data.exists) {
              equipmentRegistered.push(importResult.data.equipment?.full_name || systemData.model);
            }
          } catch (eqError) {
            // Error handled silently
            // Use a fallback equipment ID
            equipmentId = "anleon_s3d";
          }
        }
        
        // Step 2: Create the system with the equipment ID
        const modelDisplay = systemData.manufacturer 
          ? `${systemData.manufacturer} ${systemData.model}` 
          : systemData.model;
        
        // Determine valid channel_group based on manufacturer
        let validChannelGroup = systemData.channel_group || "1";
        const manufacturer = (systemData.manufacturer || "").toLowerCase();
        
        // Sennheiser uses numeric groups (1-10, U)
        // Shure, Anleon, Relacart use letter groups (A-T)
        if (manufacturer.includes("sennheiser")) {
          // If channel_group is a letter (like A, B, C), convert to numeric
          if (/^[A-Za-z]$/.test(validChannelGroup)) {
            // Map A=1, B=2, etc.
            validChannelGroup = String(validChannelGroup.toUpperCase().charCodeAt(0) - 64);
            if (parseInt(validChannelGroup) > 10) validChannelGroup = "1";
          }
        }
        
        const data = {
          name: systemData.name,
          equipment_id: equipmentId || "anleon_s3d",
          model: modelDisplay + (systemData.band ? ` (${systemData.band})` : ''),
          frequency: systemData.frequency,
          channel_group: validChannelGroup,
          channel_number: systemData.channel_number,
          band_id: "default",
          band_min: 470,
          band_max: 865,
          system_type: systemData.system_type || "microphone",
        };
        
        // Debug log removed
        await systemsAPI.create(data);
        successCount++;
      } catch (error) {
        // Error handled silently
        // Error handled silently
        errorCount++;
      }
    }
    
    setWwbImporting(false);
    setShowWWBImportDialog(false);
    setWwbImportData(null);
    setSelectedWwbSystems([]);
    
    // Refresh equipment list to include newly imported equipment
    try {
      const response = await equipmentAPI.getAll();
      setEquipmentList(response.data.equipment || []);
    } catch (err) {
      // Error handled silently
    }
    
    if (successCount > 0) {
      let message = `${successCount} sistema(s) importado(s) desde Wireless Workbench`;
      if (equipmentRegistered.length > 0) {
        message += `. Nuevos equipos registrados: ${equipmentRegistered.join(', ')}`;
      }
      toast.success(message);
      fetchSystems();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} sistema(s) no pudieron ser importados`);
    }
  };

  // Generate channel numbers based on equipment
  const channelNumbers = Array.from({ length: maxChannelNumber }, (_, i) => (i + 1).toString());

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Sistemas Inalámbricos
          </h1>
          <p className="text-sm text-[#71717A]">
            Gestión de equipos de monitoreo inalámbrico
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* WWB Import Button - All Users */}
          <input
            type="file"
            id="wwb-import-file"
            accept=".csv,.txt"
            onChange={handleWWBFileSelect}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById("wwb-import-file").click()}
            className="border-white/20 text-[#A1A1AA] hover:text-white hover:border-white/40"
            data-testid="import-wwb-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Importar WWB
          </Button>
          
          {/* Delete All Button - All Users, shows when there are systems */}
          {systems.length > 0 && (
            <Button
              onClick={() => setShowDeleteAllDialog(true)}
              variant="outline"
              className="border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-400"
              data-testid="delete-all-systems-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar Todos
            </Button>
          )}
        </div>
      </div>

      {/* View Mode Toggle and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* View Mode Toggle */}
        <div className="flex items-center bg-[#111111] border border-white/[0.08] rounded-sm p-1">
          <Button
            onClick={() => setViewMode("list")}
            variant="ghost"
            size="sm"
            className={`px-3 ${viewMode === "list" 
              ? "bg-[#34C759] text-black hover:bg-[#34C759]/90" 
              : "text-[#71717A] hover:text-white hover:bg-white/5"
            }`}
            data-testid="view-mode-list"
          >
            <LayoutList className="w-4 h-4 mr-2" />
            Lista
          </Button>
          <Button
            onClick={() => setViewMode("racks")}
            variant="ghost"
            size="sm"
            className={`px-3 ${viewMode === "racks" 
              ? "bg-[#34C759] text-black hover:bg-[#34C759]/90" 
              : "text-[#71717A] hover:text-white hover:bg-white/5"
            }`}
            data-testid="view-mode-racks"
          >
            <Package className="w-4 h-4 mr-2" />
            Racks
          </Button>
        </div>

        {/* Search - Only visible in list mode */}
        {viewMode === "list" && (
          <>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                type="text"
                placeholder="Buscar sistemas por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#111111] border-white/[0.08] text-white placeholder:text-[#71717A]"
                data-testid="search-systems-input"
              />
            </div>
            
            {/* Type Filter Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
          <TabsList className="bg-[#111111] border border-white/[0.08]">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-[#34C759]/20 data-[state=active]:text-[#34C759]"
            >
              Todos
            </TabsTrigger>
            <TabsTrigger 
              value="iem" 
              className="data-[state=active]:bg-[#34C759]/20 data-[state=active]:text-[#34C759]"
            >
              <Headphones className="w-4 h-4 mr-1" />
              IEM
            </TabsTrigger>
            <TabsTrigger 
              value="microphone" 
              className="data-[state=active]:bg-[#34C759]/20 data-[state=active]:text-[#34C759]"
            >
              <Mic className="w-4 h-4 mr-1" />
              Micrófonos
            </TabsTrigger>
          </TabsList>
        </Tabs>
          </>
        )}
      </div>

      {/* Rack View Mode */}
      {viewMode === "racks" && (
        <RackView systems={systems} />
      )}

      {/* List View Mode */}
      {viewMode === "list" && (
        <>
      {/* Equipment Overview Cards - Grouped by Type */}
      {(() => {
        // Helper to determine if equipment is IEM type
        const isIEMEquipment = (eq) => {
          const modelUpper = (eq.model || "").toUpperCase();
          const fullNameUpper = (eq.full_name || "").toUpperCase();
          const brandUpper = (eq.brand || "").toUpperCase();
          
          const iemIndicators = ["PSM", "P3T", "P9T", "P10T", "IEM", "SR20", "SR 20", "ANLEON", "RELACART", "S3D", "S3", "PM-320"];
          const micIndicators = ["EW-D", "EW100", "EW 100", "QLXD", "ULXD", "SLX", "BLX", "EM 20", "EM20", "EM-D", "SKM", "SK"];
          
          // Check for explicit mic indicators first
          for (const indicator of micIndicators) {
            if (modelUpper.includes(indicator) || fullNameUpper.includes(indicator)) {
              return false;
            }
          }
          
          // Check for IEM indicators
          for (const indicator of iemIndicators) {
            if (modelUpper.includes(indicator) || fullNameUpper.includes(indicator) || brandUpper.includes(indicator)) {
              return true;
            }
          }
          
          // Check system_type if available
          if (eq.system_type === 'iem') return true;
          if (eq.system_type === 'microphone') return false;
          
          return true; // Default to IEM
        };
        
        const iemEquipment = equipmentList.filter(isIEMEquipment);
        const micEquipment = equipmentList.filter(eq => !isIEMEquipment(eq));
        
        return (
          <div className="space-y-4 mb-6">
            {/* IEM Section - Collapsible */}
            {(activeTab === "all" || activeTab === "iem") && iemEquipment.length > 0 && (
              <div className="bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden">
                <button
                  onClick={() => toggleSection('iem')}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-[#34C759]/20 flex items-center justify-center">
                      <Headphones className="w-4 h-4 text-[#34C759]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm font-semibold text-[#F4F4F5]">Monitores In-Ear (IEM)</h3>
                      <p className="text-xs text-[#71717A]">{iemEquipment.length} equipos • {systems.filter(s => iemEquipment.some(eq => eq.id === s.equipment_id)).length} sistemas activos</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#34C759] bg-[#34C759]/10 px-2 py-1 rounded">
                      {iemEquipment.length}
                    </span>
                    {expandedSections.iem ? (
                      <ChevronDown className="w-5 h-5 text-[#71717A]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#71717A]" />
                    )}
                  </div>
                </button>
                
                {expandedSections.iem && (
                  <div className="p-4 pt-0 border-t border-white/[0.05]">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 pt-4">
                      {iemEquipment.map((eq) => {
                        const systemCount = systems.filter(s => s.equipment_id === eq.id).length;
                        return (
                          <div
                            key={eq.id}
                            className={`p-3 rounded-sm border cursor-pointer transition-all ${
                              systemCount > 0
                                ? "bg-[#34C759]/10 border-[#34C759]/30 hover:border-[#34C759]/50"
                                : "bg-[#0A0A0A] border-white/[0.05] hover:border-white/20"
                            }`}
                            onClick={() => viewEquipmentDetails(eq.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-[#F4F4F5] text-sm">{eq.brand}</p>
                                <p className="text-xs text-[#71717A]">{eq.model}</p>
                              </div>
                              <Headphones className="w-4 h-4 text-[#34C759] opacity-50" />
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className={`text-xs font-medium ${systemCount > 0 ? 'text-[#34C759]' : 'text-[#71717A]'}`}>
                                {systemCount} sistemas
                              </span>
                              <Info className="w-3 h-3 text-[#71717A]" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Microphones Section - Collapsible */}
            {(activeTab === "all" || activeTab === "microphone") && micEquipment.length > 0 && (
              <div className="bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden">
                <button
                  onClick={() => toggleSection('microphone')}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-[#8B5CF6]/20 flex items-center justify-center">
                      <Mic className="w-4 h-4 text-[#8B5CF6]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm font-semibold text-[#F4F4F5]">Micrófonos Inalámbricos</h3>
                      <p className="text-xs text-[#71717A]">{micEquipment.length} equipos • {systems.filter(s => micEquipment.some(eq => eq.id === s.equipment_id)).length} sistemas activos</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#8B5CF6] bg-[#8B5CF6]/10 px-2 py-1 rounded">
                      {micEquipment.length}
                    </span>
                    {expandedSections.microphone ? (
                      <ChevronDown className="w-5 h-5 text-[#71717A]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#71717A]" />
                    )}
                  </div>
                </button>
                
                {expandedSections.microphone && (
                  <div className="p-4 pt-0 border-t border-white/[0.05]">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 pt-4">
                      {micEquipment.map((eq) => {
                        const systemCount = systems.filter(s => s.equipment_id === eq.id).length;
                        return (
                          <div
                            key={eq.id}
                            className={`p-3 rounded-sm border cursor-pointer transition-all ${
                              systemCount > 0
                                ? "bg-[#8B5CF6]/10 border-[#8B5CF6]/30 hover:border-[#8B5CF6]/50"
                                : "bg-[#0A0A0A] border-white/[0.05] hover:border-white/20"
                            }`}
                            onClick={() => viewEquipmentDetails(eq.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-[#F4F4F5] text-sm">{eq.brand}</p>
                                <p className="text-xs text-[#71717A]">{eq.model}</p>
                              </div>
                              <Mic className="w-4 h-4 text-[#8B5CF6] opacity-50" />
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className={`text-xs font-medium ${systemCount > 0 ? 'text-[#8B5CF6]' : 'text-[#71717A]'}`}>
                                {systemCount} sistemas
                              </span>
                              <Info className="w-3 h-3 text-[#71717A]" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Systems Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#34C759] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : systems.length === 0 ? (
        <div className="text-center py-16 bg-[#111111] border border-white/[0.08] rounded-sm">
          <Radio className="w-12 h-12 mx-auto mb-4 text-[#71717A] opacity-30" />
          <h3 className="text-[#F4F4F5] font-medium mb-2">
            No hay sistemas configurados
          </h3>
          <p className="text-[#71717A] text-sm mb-4">
            Importa tus sistemas desde Wireless Workbench usando el botón "Importar WWB"
          </p>
        </div>
      ) : filteredSystems.length === 0 ? (
        <div className="text-center py-16 bg-[#111111] border border-white/[0.08] rounded-sm">
          <Search className="w-12 h-12 mx-auto mb-4 text-[#71717A] opacity-30" />
          <h3 className="text-[#F4F4F5] font-medium mb-2">
            No se encontraron sistemas
          </h3>
          <p className="text-[#71717A] text-sm mb-4">
            {searchTerm ? `No hay resultados para "${searchTerm}"` : `No hay sistemas de tipo ${activeTab === "iem" ? "IEM" : "Micrófono"}`}
          </p>
          <Button
            variant="outline"
            onClick={() => { setSearchTerm(""); setActiveTab("all"); }}
            className="border-white/20"
          >
            Limpiar filtros
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSystems.map((system) => (
            <div
              key={system.id}
              className={`bg-[#111111] border rounded-sm p-5 ${
                system.is_active
                  ? "border-white/[0.08]"
                  : "border-white/[0.04] opacity-60"
              }`}
              data-testid={`system-card-${system.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    system.system_type === "microphone" 
                      ? "bg-blue-500/20" 
                      : "bg-[#34C759]/20"
                  }`}>
                    {system.system_type === "microphone" 
                      ? <Mic className="w-4 h-4 text-blue-400" />
                      : <Headphones className="w-4 h-4 text-[#34C759]" />
                    }
                  </div>
                  <div>
                    <h3 className="font-medium text-[#F4F4F5]">{system.name}</h3>
                    <p className="text-xs text-[#71717A] mt-0.5">{system.model}</p>
                  </div>
                </div>
                <StatusBadge status={system.status} />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <p className="text-[10px] text-[#71717A] uppercase tracking-wider">
                    Frecuencia
                  </p>
                  <p className="font-mono text-sm text-[#34C759]">
                    {system.frequency.toFixed(3)} MHz
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-[#71717A] uppercase tracking-wider">
                    Canal
                  </p>
                  <p className="font-mono text-sm text-[#F4F4F5]">
                    {system.channel || `${system.channel_group}${system.channel_number}`}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-[#71717A] uppercase tracking-wider">
                    RF Level
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#1A1A1A] rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-[#34C759]"
                        style={{ width: `${system.rf_level}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#71717A]">
                      {system.rf_level}%
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-[#71717A] uppercase tracking-wider">
                    AF Level
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#1A1A1A] rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-[#3B82F6]"
                        style={{ width: `${system.af_level}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#71717A]">
                      {system.af_level}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-white/[0.08]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleActive(system)}
                  className={`flex-1 ${
                    system.is_active ? "text-[#34C759]" : "text-[#71717A]"
                  }`}
                >
                  <Power className="w-4 h-4 mr-1" />
                  {system.is_active ? "Activo" : "Inactivo"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(system)}
                  className="text-[#71717A] hover:text-[#F4F4F5]"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedSystem(system);
                    setShowDeleteDialog(true);
                  }}
                  className="text-[#71717A] hover:text-[#FF3B30]"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#111111] border-white/[0.08] text-[#F4F4F5]">
          <DialogHeader>
            <DialogTitle>
              {selectedSystem ? "Editar Sistema" : "Nuevo Sistema"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <Label>Nombre del Sistema</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Vocalista Principal"
                className="input-tactical"
                required
                data-testid="system-name-input"
              />
            </div>

            <div className="form-group">
              <Label>Tipo de Sistema</Label>
              <Select
                value={form.system_type}
                onValueChange={(value) => {
                  // Reset equipment when type changes
                  setForm({ ...form, system_type: value, equipment_id: "", frequency: "", channel_group: "A", channel_number: "1" });
                  setPresetFrequencies([]);
                }}
              >
                <SelectTrigger className="input-tactical" data-testid="system-type-select">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="iem">
                    <div className="flex items-center gap-2">
                      <Headphones className="w-4 h-4 text-[#34C759]" />
                      <span>In-Ear Monitor (IEM)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="microphone">
                    <div className="flex items-center gap-2">
                      <Mic className="w-4 h-4 text-[#3B82F6]" />
                      <span>Micrófono Inalámbrico</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#71717A] mt-1">
                Selecciona el tipo para ver solo los equipos compatibles
              </p>
            </div>

            <div className="form-group">
              <Label>Equipo</Label>
              <Select
                value={form.equipment_id}
                onValueChange={(value) => setForm({ ...form, equipment_id: value })}
              >
                <SelectTrigger className="input-tactical" data-testid="equipment-select">
                  <SelectValue placeholder="Seleccionar equipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  {equipmentList
                    .filter(eq => {
                      // Filter based on selected system type
                      if (!form.system_type) return true;
                      
                      const eqType = eq.system_type || 'iem'; // default to iem
                      const eqName = (eq.full_name || eq.model || '').toLowerCase();
                      
                      if (form.system_type === 'iem') {
                        // IEM equipment: PSM, IEM, In-Ear, Monitor, S3D, S3, etc.
                        const isIEM = eqType === 'iem' || 
                                      /\b(psm|iem|in.?ear|monitor|s3d|s3|pm.?320|sr.?2050|ew.?iem)\b/i.test(eqName);
                        const isMic = /\b(qlx|ulx|slx|blx|pgx|sm58|sm87|beta|ksm|ew.?d|skm|handheld|lavalier|lapel)\b/i.test(eqName) &&
                                     !/\b(iem|monitor|in.?ear)\b/i.test(eqName);
                        return isIEM && !isMic;
                      } else if (form.system_type === 'microphone') {
                        // Microphone equipment: QLXD, ULXD, SLX, handheld, lavalier, etc.
                        const isMic = eqType === 'microphone' ||
                                     /\b(qlx|ulx|slx|blx|pgx|sm58|sm87|beta|ksm|ew.?d|skm|handheld|lavalier|lapel|wireless.?mic)\b/i.test(eqName);
                        const isIEM = /\b(psm|iem|in.?ear|monitor)\b/i.test(eqName);
                        return isMic && !isIEM;
                      }
                      return true;
                    })
                    .map((eq) => (
                      <SelectItem key={eq.id} value={eq.id}>
                        {eq.full_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {equipmentList.filter(eq => {
                if (!form.system_type) return true;
                const eqName = (eq.full_name || eq.model || '').toLowerCase();
                if (form.system_type === 'iem') {
                  return /\b(psm|iem|in.?ear|monitor|s3d|s3|pm.?320|sr.?2050)\b/i.test(eqName) ||
                         (eq.system_type === 'iem');
                } else {
                  return /\b(qlx|ulx|slx|blx|ew.?d|skm)\b/i.test(eqName) ||
                         (eq.system_type === 'microphone');
                }
              }).length === 0 && form.system_type && (
                <p className="text-xs text-[#FFCC00] mt-1">
                  No hay equipos disponibles para este tipo. Contacta al admin para agregar más.
                </p>
              )}
            </div>

            {Object.keys(availableBands).length > 1 && (
              <div className="form-group">
                <Label>Banda de Frecuencia</Label>
                <Select
                  value={form.band_id}
                  onValueChange={(value) => setForm({ ...form, band_id: value })}
                >
                  <SelectTrigger className="input-tactical" data-testid="band-select">
                    <SelectValue placeholder="Seleccionar banda" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {Object.entries(availableBands).map(([bandId, bandInfo]) => (
                      <SelectItem key={bandId} value={bandId}>
                        {bandInfo.name} ({bandInfo.freq_min}-{bandInfo.freq_max} MHz)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Spectrum Analysis Panel */}
            {savedScans.length > 0 && (
              <div className="border border-[#34C759]/30 rounded-sm p-3 bg-[#34C759]/5">
                <button
                  type="button"
                  onClick={() => setShowSpectrumPanel(!showSpectrumPanel)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-[#34C759]">
                    <Waves className="w-4 h-4" />
                    Usar Espectro RF para Sugerir Frecuencias
                  </div>
                  {showSpectrumPanel ? <ChevronDown className="w-4 h-4 text-[#34C759]" /> : <ChevronRight className="w-4 h-4 text-[#34C759]" />}
                </button>
                
                {showSpectrumPanel && (
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-2">
                      <Select value={selectedScanId} onValueChange={setSelectedScanId}>
                        <SelectTrigger className="flex-1 input-tactical text-sm">
                          <SelectValue placeholder="Selecciona un escaneo guardado..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          {savedScans.map((scan) => (
                            <SelectItem key={scan.id} value={scan.id} className="text-sm">
                              {scan.name} ({scan.band_min}-{scan.band_max} MHz)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        onClick={analyzeSpectrum}
                        disabled={!selectedScanId || analyzingSpectrum}
                        className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
                        size="sm"
                      >
                        {analyzingSpectrum ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-1" />
                            Analizar
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {spectrumSuggestions && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[#34C759]">
                            ✓ {spectrumSuggestions.suggested_frequencies.length} frecuencias limpias
                          </span>
                          <span className="text-[#FF3B30]">
                            ✗ {spectrumSuggestions.total_occupied} ocupadas
                          </span>
                        </div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {spectrumSuggestions.suggested_frequencies.slice(0, 12).map((freq) => (
                            <button
                              key={`${freq.frequency}-${freq.group || 'default'}`}
                              type="button"
                              onClick={() => {
                                setForm(prev => ({
                                  ...prev,
                                  frequency: freq.frequency.toString(),
                                  channel_group: freq.group || prev.channel_group,
                                  channel_number: freq.channel?.toString() || prev.channel_number
                                }));
                                toast.success(`Frecuencia ${freq.frequency} MHz seleccionada`);
                              }}
                              className="w-full flex items-center justify-between px-2 py-1 text-xs bg-[#0A0A0A] hover:bg-[#1A1A1A] rounded border border-white/10"
                            >
                              <span className="font-mono text-[#F4F4F5]">{freq.label}</span>
                              <span className="text-[#34C759]">{freq.noise_level_db} dB</span>
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-[#71717A]">
                          💡 Click en una frecuencia para usarla. Las más limpias están arriba.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <Label>Frecuencia (MHz)</Label>
              {presetFrequencies.length > 0 ? (
                <Select
                  value={form.frequency}
                  onValueChange={handleFrequencySelect}
                >
                  <SelectTrigger className="input-tactical font-mono" data-testid="frequency-select">
                    <SelectValue placeholder={loadingFrequencies ? "Cargando frecuencias..." : "Selecciona frecuencia"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-60">
                    {presetFrequencies.map((freq, index) => (
                      <SelectItem 
                        key={`${freq.group}-${freq.channel}-${index}`} 
                        value={freq.frequency.toString()}
                        className="font-mono"
                      >
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="number"
                  step="0.001"
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  placeholder={`Ej: ${availableBands[form.band_id]?.freq_min || 560}.500`}
                  className="input-tactical font-mono"
                  required
                  data-testid="frequency-input"
                />
              )}
              {presetFrequencies.length > 0 && (
                <p className="text-xs text-[#34C759] mt-1">
                  ✓ Al seleccionar frecuencia, grupo y canal se asignan automáticamente
                </p>
              )}
              {availableBands[form.band_id] && (
                <p className="text-xs text-[#71717A] mt-1">
                  Rango: {availableBands[form.band_id].freq_min} - {availableBands[form.band_id].freq_max} MHz
                </p>
              )}
            </div>

            {/* Show Group and Channel as read-only when frequencies are preset */}
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Grupo {presetFrequencies.length > 0 && <span className="text-xs text-[#71717A]">(auto)</span>}</Label>
                <Select
                  value={form.channel_group}
                  onValueChange={(value) =>
                    setForm({ ...form, channel_group: value })
                  }
                  disabled={presetFrequencies.length > 0}
                >
                  <SelectTrigger className={`input-tactical ${presetFrequencies.length > 0 ? 'opacity-70' : ''}`} data-testid="channel-group-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-48">
                    {channelGroups.map((group) => (
                      <SelectItem key={group} value={group}>
                        Grupo {group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Canal {presetFrequencies.length > 0 && <span className="text-xs text-[#71717A]">(auto)</span>}</Label>
                <Select
                  value={form.channel_number}
                  onValueChange={(value) =>
                    setForm({ ...form, channel_number: value })
                  }
                  disabled={presetFrequencies.length > 0}
                >
                  <SelectTrigger className={`input-tactical ${presetFrequencies.length > 0 ? 'opacity-70' : ''}`} data-testid="channel-number-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {channelNumbers.map((num) => (
                      <SelectItem key={num} value={num}>
                        Canal {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-tactical btn-tactical-primary"
                data-testid="save-system-btn"
              >
                {selectedSystem ? "Actualizar" : "Crear Sistema"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Equipment Details Dialog */}
      <Dialog open={showEquipmentInfo} onOpenChange={setShowEquipmentInfo}>
        <DialogContent className="bg-[#111111] border-white/[0.08] text-[#F4F4F5] max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedEquipmentDetails?.full_name || "Detalles del Equipo"}
            </DialogTitle>
          </DialogHeader>
          {selectedEquipmentDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#71717A]">Marca</p>
                  <p className="font-medium">{selectedEquipmentDetails.brand}</p>
                </div>
                <div>
                  <p className="text-xs text-[#71717A]">Modelo</p>
                  <p className="font-medium">{selectedEquipmentDetails.model}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-[#71717A] mb-2">Bandas de Frecuencia</p>
                <div className="space-y-2">
                  {Object.entries(selectedEquipmentDetails.frequency_bands || {}).map(([id, band]) => (
                    <div key={id} className="p-2 bg-[#0A0A0A] rounded-sm text-sm">
                      <span className="text-[#34C759] font-mono">{band.name}</span>
                      <span className="text-[#71717A] ml-2">
                        {band.freq_min}-{band.freq_max} MHz
                      </span>
                      <span className="text-[#71717A] text-xs ml-2">({band.region})</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs text-[#71717A] mb-2">Especificaciones</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-[#0A0A0A] rounded-sm">
                    <span className="text-[#71717A]">Espaciado mínimo:</span>
                    <span className="text-[#34C759] ml-2">
                      {selectedEquipmentDetails.specifications?.min_spacing_mhz} MHz
                    </span>
                  </div>
                  <div className="p-2 bg-[#0A0A0A] rounded-sm">
                    <span className="text-[#71717A]">Sistemas máx:</span>
                    <span className="text-[#34C759] ml-2">
                      {selectedEquipmentDetails.specifications?.max_systems_recommended}
                    </span>
                  </div>
                  <div className="p-2 bg-[#0A0A0A] rounded-sm">
                    <span className="text-[#71717A]">SNR:</span>
                    <span className="text-[#F4F4F5] ml-2">
                      {selectedEquipmentDetails.specifications?.snr_db} dB
                    </span>
                  </div>
                  <div className="p-2 bg-[#0A0A0A] rounded-sm">
                    <span className="text-[#71717A]">THD:</span>
                    <span className="text-[#F4F4F5] ml-2">
                      {selectedEquipmentDetails.specifications?.thd_percent}%
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-[#71717A] mb-2">Características</p>
                <div className="flex flex-wrap gap-2">
                  {selectedEquipmentDetails.features?.map((feature) => (
                    <span
                      key={feature}
                      className="px-2 py-1 bg-[#34C759]/20 text-[#34C759] text-xs rounded-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {selectedEquipmentDetails.notes && (
                <p className="text-sm text-[#71717A] italic">
                  {selectedEquipmentDetails.notes}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#111111] border-white/[0.08]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F4F4F5]">
              Eliminar Sistema
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              ¿Estás seguro de eliminar "{selectedSystem?.name}"? Esta acción no
              se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-[#F4F4F5]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Systems Confirmation Dialog */}
      <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <AlertDialogContent className="bg-[#111111] border-white/[0.08]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F4F4F5] flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-[#FF3B30]" />
              Eliminar Todos los Sistemas
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              ¿Estás seguro de eliminar <span className="text-white font-bold">{systems.length} sistemas</span>? 
              Esta acción eliminará todos tus sistemas configurados y no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border-white/10 text-[#F4F4F5]"
              disabled={deletingAll}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllSystems}
              disabled={deletingAll}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80"
            >
              {deletingAll ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Eliminando...
                </>
              ) : (
                <>Eliminar {systems.length} Sistemas</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Wireless Workbench Import Dialog */}
      <Dialog open={showWWBImportDialog} onOpenChange={setShowWWBImportDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#8B5CF6]" />
              Importar desde Wireless Workbench
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Los equipos detectados se registrarán automáticamente en el sistema
            </DialogDescription>
          </DialogHeader>

          {wwbImportError ? (
            <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FF3B30] flex-shrink-0 mt-0.5" />
              <p className="text-[#FF3B30] text-sm">{wwbImportError}</p>
            </div>
          ) : wwbImportData ? (
            <div className="space-y-4">
              {/* Summary of detected equipment */}
              <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                <p className="text-xs text-[#71717A] mb-2">Equipos detectados:</p>
                <div className="flex flex-wrap gap-2">
                  {[...new Set(wwbImportData.map(s => 
                    s.manufacturer ? `${s.manufacturer} ${s.model}` : s.model
                  ))].map((model) => (
                    <span key={model} className="px-2 py-1 bg-[#8B5CF6]/20 text-[#8B5CF6] text-xs rounded-sm font-mono">
                      {model}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Select All */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#71717A]">
                  {wwbImportData.length} sistema(s) encontrado(s)
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (selectedWwbSystems.length === wwbImportData.length) {
                      setSelectedWwbSystems([]);
                    } else {
                      setSelectedWwbSystems(wwbImportData.map((_, i) => i));
                    }
                  }}
                  className="text-[#8B5CF6] hover:text-[#8B5CF6] text-xs"
                >
                  {selectedWwbSystems.length === wwbImportData.length ? "Deseleccionar todo" : "Seleccionar todo"}
                </Button>
              </div>
              
              {/* Systems List - Table format */}
              <div className="border border-white/[0.08] rounded-sm overflow-hidden">
                <div className="grid grid-cols-12 gap-2 p-2 bg-[#0A0A0A] text-xs text-[#71717A] font-medium">
                  <div className="col-span-1"></div>
                  <div className="col-span-3">Nombre</div>
                  <div className="col-span-2">Modelo</div>
                  <div className="col-span-2">Banda</div>
                  <div className="col-span-2">Grupo/CH</div>
                  <div className="col-span-2 text-right">Frecuencia</div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {wwbImportData.map((system, idx) => (
                    <div
                      key={`${system.name}-${system.frequency}-${idx}`}
                      onClick={() => toggleWwbSystemSelection(idx)}
                      className={`grid grid-cols-12 gap-2 p-2 cursor-pointer transition-colors border-t border-white/[0.05] ${
                        selectedWwbSystems.includes(idx)
                          ? "bg-[#8B5CF6]/10"
                          : "hover:bg-white/[0.02]"
                      }`}
                    >
                      <div className="col-span-1 flex items-center justify-center">
                        <div className={`w-4 h-4 rounded flex items-center justify-center ${
                          selectedWwbSystems.includes(idx)
                            ? "bg-[#8B5CF6]"
                            : "bg-white/10"
                        }`}>
                          {selectedWwbSystems.includes(idx) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                      </div>
                      <div className="col-span-3 text-sm text-white truncate">{system.name}</div>
                      <div className="col-span-2 text-xs text-[#8B5CF6] font-mono">{system.model}</div>
                      <div className="col-span-2 text-xs text-[#71717A]">{system.band || '-'}</div>
                      <div className="col-span-2 text-xs text-[#71717A]">
                        {system.group_channel || `G:${system.channel_group} CH:${system.channel_number}`}
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="font-mono text-sm text-[#34C759]">{system.frequency.toFixed(3)}</span>
                        <span className="text-[10px] text-[#71717A] ml-1">MHz</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Import Info */}
              <div className="p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                <p className="text-xs text-[#34C759]">
                  Los equipos nuevos (ej: Shure QLXD4) se registrarán automáticamente como marcas disponibles en tu cuenta.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowWWBImportDialog(false);
                    setWwbImportData(null);
                    setSelectedWwbSystems([]);
                  }}
                  className="text-[#A1A1AA] hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmWWBImport}
                  disabled={wwbImporting || selectedWwbSystems.length === 0}
                  className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 text-white"
                  data-testid="confirm-wwb-import-btn"
                >
                  {wwbImporting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar {selectedWwbSystems.length} Sistema(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="systems"
        currentPlan={subscriptionUsage?.plan_name || "Gratuito"}
        limit={subscriptionUsage?.usage?.systems?.limit}
        current={subscriptionUsage?.usage?.systems?.current}
      />
    </Layout>
  );
}
